import { UntilDestroy } from '@ngneat/until-destroy';
import { Injectable } from '@angular/core';
import { Route } from 'dataModels/route';
import { Page } from 'dataModels/page';
import { WorkDaysFromHomeNavigationService } from 'services/navigation/work-days-from-home-navigation.service';
import { EmploymentStatusNavigationService } from 'services/navigation/employment-status-navigation.service';
import { PageRouteMapKeys } from 'sharedModels/page-route-map';
import { INavigationService } from 'sharedModels/interfaces/navigation-service';
import { Store } from '@ngrx/store';
import { selectQuoteFlowType } from 'stores/quote/quote.selectors';
import { RiskAddressNavigationService } from './riskAddressNavigation.service';
import { EmbeddedCashbackNavigationService } from './embedded-cashback-navigation.service';

/**
 * Represents the result of conditional page activation.
 */
export class ConditionalPageActivationResult {
    /**
     * Constructor for ConditionalPageActivationResult.
     * @param canActivate - Indicates whether the page can be activated.
     * @param route - The route to navigate to if activation is allowed.
     */
    constructor(
        public canActivate: boolean = true,
        public route: Route | null = null,
    ) {}
}

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class NavigationService implements INavigationService {
    private readonly quoteFlowType = this.store$.selectSignal(selectQuoteFlowType);
    /**
     * Initializes the NavigationService with required navigation dependencies.
     * @param workDaysFromHomeNavigationService$ Service for handling navigation logic related to "Work Days From Home" pages.
     * @param employmentStatusNavigationService$ Service for handling navigation logic related to employment status pages.
     * @param vehicleRiskAddressNavigationService$ Service for handling navigation logic related to vehicle risk address pages.
     * @param embeddedCashbackNavigationService$ Service for handling navigation logic related to embedded cashback pages.
     */
    public constructor(
        private readonly workDaysFromHomeNavigationService$: WorkDaysFromHomeNavigationService,
        private readonly employmentStatusNavigationService$: EmploymentStatusNavigationService,
        private readonly riskAddressNavigationService$: RiskAddressNavigationService,
        private readonly embeddedCashbackNavigationService$: EmbeddedCashbackNavigationService,
        private readonly store$: Store,
    ) {}

    /**
     * Resolves conditional page activation based on the provided page.
     * @param page - The page to resolve activation for.
     * @returns A `ConditionalPageActivationResult` containing the activation status and route.
     */
    public resolveConditionalPageActivation(page: Page): ConditionalPageActivationResult {
        const fixedReturnValue: ConditionalPageActivationResult = {
            canActivate: true,
            route: null as Route | null,
        };

        let dynamicReturnValue: ConditionalPageActivationResult = null;

        // If the page is disabled, set activation to false and route to null.
        if (page.pageDisabled) {
            fixedReturnValue.canActivate = false;
            fixedReturnValue.route = null;
        }

        const pageName = this.pageName(page);
        // Handle specific page names using dedicated navigation services.
        switch (pageName) {
            case PageRouteMapKeys.WorkdaysFromHome:
            case PageRouteMapKeys.HouseholdMemberWorkdaysFromHome:
                dynamicReturnValue =
                    this.workDaysFromHomeNavigationService$.resolveConditionalPageActivation(
                        pageName,
                    );
                break;
            default:
                break;
        }
        return dynamicReturnValue ?? fixedReturnValue;
    }

    /**
     * Resolves the conditional button route for the provided page and route.
     * @param page - The page to resolve the button route for.
     * @param route - The default route to be used.
     * @returns An object containing the resolved route and a flag indicating whether the route was resolved.
     */
    public resolveConditionalButtonRoute(page: Page, route: Route) {
        const pageName = this.pageName(page);

        // Note: we are manually setting resolvedRoute to true when we hit a switch case statement so that we know
        // if this method has resolved the route or not.
        let resolvedRoute = false;

        // Handle specific page names using dedicated navigation services.
        switch (pageName) {
            case PageRouteMapKeys.EmploymentStatus:
            case PageRouteMapKeys.HouseholdMemberEmploymentStatus:
                route =
                    this.employmentStatusNavigationService$.resolveConditionalButtonRoutePolicyHolder(
                        pageName,
                        route,
                    );
                resolvedRoute = true;
                break;

            case PageRouteMapKeys.CashbackDetailsPage:
                route =
                    this.embeddedCashbackNavigationService$.resolveConditionalPageActivation(route);

                resolvedRoute = true;
                break;
            default:
                break;
        }

        return { route, resolvedRoute };
    }

    /**
     * Retrieves the normalized name of the page by removing whitespace.
     * @param page - The page to retrieve the name for.
     * @returns The normalized page name.
     */
    private pageName(page: Page): string {
        return page?.name?.replace(/\s+/g, '').toLowerCase();
    }
}
